/*
 * Pure helper functions for resolving gallery state in the PDP Configuration Selector.
 * These functions are mode-aware: choose-layout and customize modes have different
 * gallery state priority rules and must never bleed into each other.
 *
 * No React, no hooks — safe to call from useMemo or outside render.
 */

import {
  buildHeroGalleryLayerStack,
  COVER_SUBVIEW_TOUCH_PRIORITY,
  EPHEMERAL_GALLERY_TOUCH_PRIORITY,
  buildLayerFromProduct,
  buildLayersFromSelections,
} from '@amblique/king-living/app/utils/gallery-resource-utils';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';

/**
 * @param {Object|null|undefined} product
 * @returns {string|null}
 */
function getResolvedProductId(product) {
  if (!product) return null;

  const id = product.id ?? product.productId;

  return id != null ? String(id) : null;
}

/**
 * @param {string|null|undefined} selectedCoverId
 * @param {Object|null|undefined} coverVariantProduct
 * @returns {boolean}
 */
function isSimplePdpCoverGallerySettled(selectedCoverId, coverVariantProduct) {
  if (!selectedCoverId) return true;

  const resolvedId = getResolvedProductId(coverVariantProduct);

  return resolvedId !== null && resolvedId === String(selectedCoverId);
}

/**
 * @param {Object|null|undefined} optionPreviewProduct
 * @returns {boolean}
 */
function isSimplePdpOptionGallerySettled(optionPreviewProduct) {
  if (!optionPreviewProduct) return true;

  return getResolvedProductId(optionPreviewProduct) !== null;
}

/**
 * Cover vs option recency from committed selections (`selectedAt`; ties → last STEPS slot).
 *
 * @param {Object|null|undefined} selections
 * @param {Array<{id?: string}>|null|undefined} steps
 * @returns {{driver: 'standing'|'cover'|'option', optionProductId: string|null}}
 */
function resolveSimplePdpCommittedGalleryRecency(selections, steps) {
  if (!selections || typeof selections !== 'object') {
    return {driver: 'standing', optionProductId: null};
  }

  let bestAt = -Infinity;
  let driver = 'standing';
  let optionProductId = null;

  const cover = selections.cover;

  if (cover?.productId) {
    const coverAt = typeof cover.selectedAt === 'number' ? cover.selectedAt : 0;

    if (coverAt >= bestAt) {
      bestAt = coverAt;
      driver = 'cover';
      optionProductId = null;
    }
  }

  if (steps?.length) {
    steps.forEach((step) => {
      if (!step?.id || step.id === 'cover') return;

      const slotSelections = selections[step.id];

      if (!Array.isArray(slotSelections)) return;

      slotSelections.forEach((slot) => {
        const productId = slot?.productId;

        if (!productId || slot.isAutoSelected === true) return;

        const selectedAt = typeof slot.selectedAt === 'number' ? slot.selectedAt : 0;

        if (selectedAt >= bestAt) {
          bestAt = selectedAt;
          driver = 'option';
          optionProductId = productId;
        }
      });
    });
  }

  return {driver, optionProductId};
}

/**
 * @param {Object|null|undefined} selections
 * @param {Array<{id?: string}>|null|undefined} steps
 * @returns {string|null}
 */
export function resolveMostRecentCommittedOptionProductId(selections, steps) {
  return resolveSimplePdpCommittedGalleryRecency(selections, steps).optionProductId;
}

/**
 * @param {Object|null|undefined} selections
 * @param {Array<{id?: string}>|null|undefined} steps
 * @returns {'standing'|'cover'|'option'}
 */
export function resolveSimplePdpHeroDriverFromCommittedSelections(selections, steps) {
  return resolveSimplePdpCommittedGalleryRecency(selections, steps).driver;
}

/**
 * Intended main-gallery state for Simple PDP with configurable options (settled gate).
 * Caller holds snapshot when `settled === false`.
 *
 * @param {Object} params
 * @param {'standing'|'cover'|'option'|'add-on'} [params.heroDriver]
 * @param {string|null} [params.selectedCoverId]
 * @param {Object|null} [params.coverVariantProduct] - SCAPI cover variant
 * @param {Object|null} [params.optionPreviewProduct]
 * @param {Object|null} [params.addOnDetailsProduct]
 * @param {Object|null} [params.coverVariationValues]
 * @param {Object|null} [params.committedSelections]
 * @param {Object|null} [params.masterProduct]
 * @param {boolean} [params.useCustomizePricing]
 * @returns {{
 *   settled: boolean,
 *   galleryVariationAttributes: Object,
 *   layers: Array,
 *   previewProduct: Object|null,
 *   viewDetailsProduct: Object|null,
 * }}
 */
export function resolveSimplePdpMainGalleryState({
  heroDriver = 'standing',
  selectedCoverId = null,
  coverVariantProduct = null,
  optionPreviewProduct = null,
  addOnDetailsProduct = null,
  coverVariationValues = null,
  committedSelections = null,
  masterProduct = null,
  useCustomizePricing = false,
}) {
  const coverSettled = isSimplePdpCoverGallerySettled(selectedCoverId, coverVariantProduct);
  const optionSettled = isSimplePdpOptionGallerySettled(optionPreviewProduct);
  const addOnSettled = isSimplePdpOptionGallerySettled(addOnDetailsProduct);

  const settled =
    heroDriver === 'standing' ||
    (heroDriver === 'add-on' && addOnDetailsProduct != null && addOnSettled) ||
    (heroDriver === 'cover' && coverSettled) ||
    (heroDriver === 'option' && optionPreviewProduct != null && optionSettled);

  const coverAttrs =
    coverVariationValues && typeof coverVariationValues === 'object' ? coverVariationValues : {};

  const galleryVariationAttributes = {...coverAttrs};

  let layers = [];
  let previewProduct = null;
  let viewDetailsProduct = null;

  if (heroDriver === 'add-on' && addOnDetailsProduct) {
    layers = [];
    previewProduct = addOnDetailsProduct;
    viewDetailsProduct = addOnDetailsProduct;
  } else if (heroDriver === 'cover') {
    layers = [];
    previewProduct = null;
    viewDetailsProduct = coverSettled ?
      coverVariantProduct :
      (selectedCoverId ? getSelectedVariantProduct(masterProduct, selectedCoverId) : null);
  } else if (heroDriver === 'option' && optionPreviewProduct) {
    const base = useCustomizePricing && committedSelections ?
      buildLayersFromSelections(committedSelections) :
      [];

    previewProduct = optionPreviewProduct;
    layers = optionPreviewProduct !== masterProduct ?
      [...base, buildLayerFromProduct(optionPreviewProduct)] :
      base;
    viewDetailsProduct = optionPreviewProduct;
  } else if (useCustomizePricing && committedSelections) {
    layers = buildLayersFromSelections(committedSelections);
  }

  return {
    galleryVariationAttributes,
    layers,
    previewProduct,
    settled,
    viewDetailsProduct,
  };
}

/**
 * @param {Object|null|undefined} cover - Cover step selection object
 * @returns {boolean}
 */
function hasCoverSelection(cover) {
  if (!cover || typeof cover !== 'object') return false;
  if (cover.productId) return true;
  const vv = cover.variationValues;

  return vv && typeof vv === 'object' && Object.keys(vv).length > 0;
}

/**
 * @param {Object|null|undefined} slot
 * @returns {boolean}
 */
function isUserVisibleCommittedOptionSlot(slot) {
  return Boolean(slot?.productId) && slot.isAutoSelected !== true;
}

/**
 * @param {Object|null|undefined} selections
 * @returns {boolean}
 */
export function hasCommittedSelectionData(selections) {
  if (!selections || typeof selections !== 'object' || Object.keys(selections).length === 0) {
    return false;
  }

  if (hasCoverSelection(selections.cover)) {
    return true;
  }

  return Object.entries(selections).some(([key, value]) => {
    if (key === 'cover' || !Array.isArray(value)) return false;

    return value.some((slot) => Boolean(slot?.productId));
  });
}

/**
 * Like {@link hasCommittedSelectionData} but excludes hidden auto-selected option slots
 * (`isAutoSelected` from useAutoSelectHiddenSlots). Used for ViewDetails visibility —
 * auto-hidden preselects must still ship in cart without surfacing LHS product content.
 *
 * @param {Object|null|undefined} selections
 * @returns {boolean}
 */
export function hasUserVisibleCommittedSelectionData(selections) {
  if (!selections || typeof selections !== 'object' || Object.keys(selections).length === 0) {
    return false;
  }

  if (hasCoverSelection(selections.cover)) {
    return true;
  }

  return Object.entries(selections).some(([key, value]) => {
    if (key === 'cover' || key === 'optionalAddOns' || !Array.isArray(value)) return false;

    return value.some(isUserVisibleCommittedOptionSlot);
  });
}

/**
 * Complex customize flyout LHS — show committed Cylindo-priority gallery on reopen
 * before the user interacts (draft preview takes over after interaction).
 *
 * @param {Object} params
 * @param {string} params.configPanelMode
 * @param {boolean} [params.flyoutIsOpen]
 * @param {boolean} [params.hasUserInteracted]
 * @param {string|null|undefined} [params.committedMode]
 * @param {Object|null|undefined} [params.committedSelections]
 * @returns {boolean}
 */
export function shouldShowCommittedFlyoutGallery({
  configPanelMode,
  flyoutIsOpen = false,
  hasUserInteracted = false,
  committedMode,
  committedSelections,
}) {
  return configPanelMode === 'customize' &&
    flyoutIsOpen &&
    !hasUserInteracted &&
    committedSelections &&
    Object.keys(committedSelections).length > 0 &&
    (committedMode === 'set' || committedMode === 'customize');
}

/**
 * Whether to use layer-stack gallery priority (PIM video > PIM > Cylindo > SFCC,
 * last layer wins). When false, the PDP shows standing hero resources only
 * (variant/master hero group from `c_processedImageGroups`, mirroring ImageGallery V1).
 *
 * Auto-preselect cover counts as interaction (committed `selections.cover`).
 * URL `variationParams` alone do not enable layer priority.
 *
 * @param {Object} params
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} [params.selections]
 * @param {Array<{cylindoFeature?: string|null, pimImages?: Array|null}>} [params.layers]
 * @param {Object|null} [params.previewProduct]
 * @param {Object|null} [params.masterProduct]
 * @param {boolean} [params.isCommittedSetOrCustomize]
 * @returns {boolean}
 */
export function shouldApplyGalleryLayerPriority({
  selections,
  layers,
  previewProduct,
  masterProduct,
  isCommittedSetOrCustomize = false,
}) {
  if (isCommittedSetOrCustomize) return true;
  if (hasCoverSelection(selections?.cover)) return true;
  if (Array.isArray(layers) && layers.length > 0) return true;
  if (previewProduct && previewProduct !== masterProduct) return true;

  return false;
}

/**
 * Resolves variation attributes to use for the ImageGallery display.
 *
 * choose-layout : always variationParams — no context reads, no draft state
 * customize     : priority chain — draftCover → committedCover → galleryPreview → variationParams
 *
 * @param {Object} params
 * @param {string} params.configPanelMode                - 'choose-layout' | 'customize'
 * @param {Object} [params.flyoutDraftSelections]        - flyoutDraftState.draftSelections
 * @param {Object} [params.committedSelections]          - committedSelection.selections
 * @param {Object} [params.galleryPreviewVariationValues] - flyoutDraftState.galleryPreview?.variationValues
 * @param {Object} [params.variationParams]              - base product variationParams from ProductView
 * @returns {Object} variation attributes object
 */
export function resolveGalleryVariationAttributes({
  configPanelMode,
  flyoutDraftSelections,
  committedSelections,
  galleryPreviewVariationValues,
  variationParams,
}) {
  // choose-layout: always base product, never touch context state
  if (configPanelMode !== 'customize') {
    return variationParams ?? {};
  }

  const draftCover = flyoutDraftSelections?.cover?.variationValues;

  if (draftCover && typeof draftCover === 'object' && Object.keys(draftCover).length > 0) {
    return draftCover;
  }

  const committedCover = committedSelections?.cover?.variationValues;

  if (committedCover && typeof committedCover === 'object' && Object.keys(committedCover).length > 0) {
    return committedCover;
  }

  if (galleryPreviewVariationValues && Object.keys(galleryPreviewVariationValues).length > 0) {
    return galleryPreviewVariationValues;
  }

  return variationParams ?? {};
}

/**
 * Resolves which product object to show in the gallery.
 *
 * choose-layout : selectedConfigProduct (or master product) — galleryPreview never applies
 * customize     : galleryPreview.product overrides when set
 *
 * @param {Object} params
 * @param {string} params.configPanelMode           - 'choose-layout' | 'customize'
 * @param {Object|null} [params.galleryPreviewProduct] - flyoutDraftState.galleryPreview?.product
 * @param {Object|null} [params.selectedConfigProduct] - config product matching effectiveSelectedId
 * @param {Object} [params.product]                 - master product (fallback)
 * @returns {Object|undefined} product to pass to ImageGallery
 */
export function resolveGalleryProduct({
  configPanelMode,
  galleryPreviewProduct,
  selectedConfigProduct,
  product,
}) {
  // choose-layout: galleryPreview is never used (it belongs to customize mode only)
  if (configPanelMode !== 'customize') {
    return selectedConfigProduct ?? product;
  }

  return galleryPreviewProduct ?? selectedConfigProduct ?? product;
}

/**
 * True when previewProduct is a real gallery preview, not the master product synced on flyout open.
 *
 * @param {Object|null|undefined} previewProduct
 * @param {string|null|undefined} masterProductId
 * @returns {boolean}
 */
export function isMeaningfulProductPreview(previewProduct, masterProductId) {
  if (!previewProduct) return false;

  const previewId = previewProduct?.id ?? previewProduct?.productId;

  if (!previewId) return false;
  if (!masterProductId) return true;

  return String(previewId) !== String(masterProductId);
}

/**
 * @param {Object|null|undefined} previewValues
 * @param {Object|null|undefined} baselineValues
 * @returns {boolean}
 */
function isVariationPreviewActive(previewValues, baselineValues) {
  const preview = previewValues && typeof previewValues === 'object' ? previewValues : {};
  const baseline = baselineValues && typeof baselineValues === 'object' ? baselineValues : {};

  if (Object.keys(preview).length === 0) {
    return false;
  }

  const keys = new Set([...Object.keys(preview), ...Object.keys(baseline)]);

  for (const key of keys) {
    if (String(preview[key] ?? '') !== String(baseline[key] ?? '')) {
      return true;
    }
  }

  return false;
}

/**
 * Resolves whether to show the ViewDetails section (title, description, thumbnails)
 * below the ImageGallery inside ReviewSection.
 *
 * choose-layout : true whenever a product is loaded (effectiveSelectedId always non-null
 *                 when product is loaded — selectedConfigId ?? product.id ?? product.productId)
 * customize     : after user interaction, show while viewDetailsProduct is a meaningful
 *                 preview or gallery variation values differ from committed baseline
 *
 * @param {Object} params
 * @param {string} params.configPanelMode
 * @param {string|null} [params.effectiveSelectedId]   - selectedConfigId ?? product?.id ?? product?.productId
 * @param {string|null|undefined} [params.masterProductId]
 * @param {Object|null|undefined} [params.viewDetailsProduct] - resolved product driving ViewDetails
 * @param {Object} [params.previewVariationValues]
 * @param {Object} [params.baselineVariationValues] - committed cover variation baseline
 * @param {boolean} [params.hasCustomizeInteraction] - flyout hasUserInteracted or inline picker open
 * @returns {boolean}
 */
export function resolveShowViewDetailsSection({
  configPanelMode,
  effectiveSelectedId,
  masterProductId,
  viewDetailsProduct,
  previewVariationValues,
  baselineVariationValues,
  hasCustomizeInteraction = false,
}) {
  // choose-layout: always visible when product is loaded
  if (configPanelMode !== 'customize') {
    return effectiveSelectedId != null;
  }

  if (!hasCustomizeInteraction) {
    return false;
  }

  if (isMeaningfulProductPreview(viewDetailsProduct, masterProductId)) {
    return true;
  }

  return isVariationPreviewActive(previewVariationValues, baselineVariationValues);
}

/**
 * Complex flyout hero preview from journey context — shared by mirror sync, desktop LHS,
 * mobile drawer header, and openViewDetails galleryPreview writes.
 * Cover variant SCAPI is for ViewDetails only — not appended as a hero preview layer.
 */
export function resolveComplexFlyoutHeroPreviewFromJourney({
  galleryPreviewProduct,
  masterProduct,
  draftSelections,
  committedSelections,
}) {
  if (!galleryPreviewProduct || galleryPreviewProduct === masterProduct) return null;

  const previewId = galleryPreviewProduct?.id ?? galleryPreviewProduct?.productId;
  const draftCover = draftSelections?.cover;
  const coverSelection = draftCover?.productId ? draftCover : (committedSelections?.cover ?? null);
  const coverId = coverSelection?.productId;

  if (coverId != null && previewId != null && String(previewId) === String(coverId)) {
    return null;
  }

  return galleryPreviewProduct;
}

function mergeSelectionsForGalleryDisplay(committedSelections, draftSelections) {
  if (!committedSelections && !draftSelections) return null;
  if (!draftSelections) return committedSelections ?? null;
  if (!committedSelections) return draftSelections;

  return {...committedSelections, ...draftSelections};
}

function collectGalleryTouchEntries({
  effectiveSelections,
  galleryPreviewProduct,
  masterProduct,
}) {
  const masterProductId = getResolvedProductId(masterProduct);
  const entries = [];
  const cover = effectiveSelections?.cover;

  if (hasCoverSelection(cover)) {
    entries.push({
      channel: 'cover',
      coverSelection: cover,
      id: `cover:${cover?.productId ?? 'attrs'}`,
      selectedAt: typeof cover?.selectedAt === 'number' ? cover.selectedAt : 0,
    });
  }

  if (effectiveSelections && typeof effectiveSelections === 'object') {
    Object.entries(effectiveSelections).forEach(([key, value]) => {
      if (key === 'cover' || !Array.isArray(value)) return;

      value.forEach((slot, slotIndex) => {
        if (!isUserVisibleCommittedOptionSlot(slot)) return;

        entries.push({
          channel: 'product',
          id: `slot:${key}:${slotIndex}:${slot.productId}`,
          selectedAt: typeof slot.selectedAt === 'number' ? slot.selectedAt : 0,
          slot,
        });
      });
    });
  }

  const coverId = cover?.productId;
  const previewId = galleryPreviewProduct?.id ?? galleryPreviewProduct?.productId;
  const isCoverPreview =
    coverId != null && previewId != null && String(previewId) === String(coverId);

  if (isMeaningfulProductPreview(galleryPreviewProduct, masterProductId) && !isCoverPreview) {
    entries.push({
      channel: 'product',
      id: `preview:${previewId}`,
      previewProduct: galleryPreviewProduct,
      selectedAt: EPHEMERAL_GALLERY_TOUCH_PRIORITY,
    });
  }

  return entries;
}

function withCoverSubViewTouchPriority(touchEntries, isCoverPickerOpen = false) {
  if (!isCoverPickerOpen || !touchEntries?.length) return touchEntries;

  return touchEntries.map((entry) => {
    if (entry.channel === 'cover' && entry.coverSelection?.productId) {
      return {...entry, selectedAt: COVER_SUBVIEW_TOUCH_PRIORITY};
    }

    return entry;
  });
}

/**
 * @param {Array<{selectedAt: number}>} touchEntries
 * @returns {Object|null}
 */
function resolveLatestGalleryTouchEntry(touchEntries) {
  if (!touchEntries?.length) return null;

  let latest = touchEntries[0];
  let bestAt = typeof latest.selectedAt === 'number' ? latest.selectedAt : 0;

  touchEntries.forEach((entry) => {
    const at = typeof entry.selectedAt === 'number' ? entry.selectedAt : 0;

    if (at >= bestAt) {
      bestAt = at;
      latest = entry;
    }
  });

  return latest;
}

/**
 * Complex customize flyout gallery facade — unified touch layer stack (cover / option / preview equal).
 *
 * @param {Object} params
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} [params.committedSelections]
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} [params.draftSelections]
 * @param {Object|null|undefined} params.masterProduct
 * @param {Object|null|undefined} [params.galleryPreviewProduct]
 * @param {boolean} [params.isCoverPickerOpen]
 * @returns {{
 *   galleryVariationAttributes: Object,
 *   galleryLayers: Array,
 *   latestHeroProduct: Object|null,
 * }}
 */
export function resolveComplexGallery({
  committedSelections,
  draftSelections,
  masterProduct,
  galleryPreviewProduct = null,
  isCoverPickerOpen = false,
}) {
  const effectiveSelections = mergeSelectionsForGalleryDisplay(
    committedSelections,
    draftSelections
  );
  const touchEntries = withCoverSubViewTouchPriority(
    collectGalleryTouchEntries({
      effectiveSelections,
      galleryPreviewProduct,
      masterProduct,
    }),
    isCoverPickerOpen
  );
  const latestTouch = resolveLatestGalleryTouchEntry(touchEntries);
  const coverSelection = effectiveSelections?.cover ?? null;
  const coverAttrs =
    coverSelection?.variationValues && typeof coverSelection.variationValues === 'object' ?
      coverSelection.variationValues :
      {};

  return {
    galleryLayers: buildHeroGalleryLayerStack({masterProduct, touchEntries}),
    galleryVariationAttributes: {...coverAttrs},
    latestHeroProduct: latestTouch?.previewProduct ?? null,
  };
}

/**
 * Gallery layer + ViewDetails resolution inside CustomizeStepsContent.
 *
 * Flyout (Complex / !isSimplePDP): sticky committed cover only after hasUserInteracted
 * or an open sub-view (activeSlot) — matches flyout draft semantics.
 *
 * Inline Simple PDP: committed cover (manual select or pre-select) is enough; cover grid
 * may live outside CustomizeStepsInline (Configurable Master).
 *
 * viewDetailsProduct priority: option preview > cover SCAPI (cover/main context) > Simple committed option fallback.
 * Simple PDP cover SCAPI pending: suppress stale option preview; hold previous cover SCAPI via keepPreviousData
 * until the new cover id resolves (no option / catalog fallback during the gap).
 *
 * @param {Object} params
 * @param {boolean} [params.isSimplePDP]
 * @param {boolean} [params.hasUserInteracted]
 * @param {{stepId: string, slotIndex: number}|null} [params.activeSlot]
 * @param {boolean} [params.isCoverPickerOpen]
 * @param {string|null} [params.selectedCoverId]
 * @param {Object|null} [params.galleryPreviewProduct]
 * @param {Object|null} [params.coverVariantProduct]
 * @param {Object|null} [params.mostRecentCommittedOptionProduct]
 * @param {Object|null} [params.masterProduct]
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} [params.draftSelections]
 * @param {import('../context/customize-journey-context').SelectionsMap|null|undefined} [params.committedSelections]
 * @returns {{galleryDisplayProduct: Object|null, mirrorGalleryProduct: Object|null, viewDetailsProduct: Object|null}}
 */
export function resolveCustomizeStepsGalleryState({
  isSimplePDP = false,
  hasUserInteracted = false,
  activeSlot = null,
  isCoverPickerOpen = false,
  selectedCoverId = null,
  galleryPreviewProduct = null,
  coverVariantProduct = null,
  mostRecentCommittedOptionProduct = null,
  masterProduct = null,
  draftSelections = null,
  committedSelections = null,
}) {
  const coverVariantId = coverVariantProduct?.id ?? coverVariantProduct?.productId ?? '';
  const isOptionSlotPreviewActive = isSimplePDP && activeSlot != null &&
    activeSlot.stepId !== 'cover' &&
    galleryPreviewProduct != null;
  const suppressOptionPreview = isSimplePDP && Boolean(selectedCoverId) && !isOptionSlotPreviewActive && (
    !coverVariantProduct || String(coverVariantId) !== String(selectedCoverId)
  );
  const effectiveGalleryPreviewProduct = suppressOptionPreview ? null : galleryPreviewProduct;
  const inlineCommittedCoverActive = isSimplePDP && Boolean(selectedCoverId);
  const shouldKeepStickyGalleryLayer = activeSlot != null ||
    (isSimplePDP ? inlineCommittedCoverActive : hasUserInteracted);
  const stickyLayerProduct =
    isCoverPickerOpen || !shouldKeepStickyGalleryLayer ? null : coverVariantProduct;
  // Complex flyout cover picker open: ViewDetails / layer context only — hero uses attrs, not cover SCAPI.
  // Without this, galleryPreview stays master → option slot remains last layer → stale c_video.
  const complexCoverPickerGalleryProduct =
    !isSimplePDP && isCoverPickerOpen ? coverVariantProduct : null;
  const committedInlineDetailsProduct = isSimplePDP ?
    (selectedCoverId ? coverVariantProduct : (mostRecentCommittedOptionProduct ?? coverVariantProduct)) :
    null;
  const viewDetailsProduct = effectiveGalleryPreviewProduct ??
    complexCoverPickerGalleryProduct ??
    committedInlineDetailsProduct ??
    (!isSimplePDP ? stickyLayerProduct : null);
  const galleryDisplayProduct = effectiveGalleryPreviewProduct ??
    complexCoverPickerGalleryProduct ??
    stickyLayerProduct ??
    masterProduct;
  const mirrorGalleryProduct = isSimplePDP ?
    effectiveGalleryPreviewProduct :
    resolveComplexGallery({
      committedSelections,
      draftSelections,
      galleryPreviewProduct: effectiveGalleryPreviewProduct,
      isCoverPickerOpen,
      masterProduct,
    }).latestHeroProduct ?? null;

  return {galleryDisplayProduct, mirrorGalleryProduct, viewDetailsProduct};
}

/**
 * ViewDetails product for Simple PDP main gallery (product-view ReviewSection).
 * Folds committed cover when the inline mirror is empty (cover grid outside inline session).
 *
 * @param {Object} params
 * @param {Object|null} [params.addOnDetailsProduct]
 * @param {Object|null|undefined} [params.mirrorViewDetailsProduct]
 * @param {Object|null|undefined} [params.committedCoverProduct]
 * @param {Object|null|undefined} [params.masterProduct]
 * @param {boolean} [params.hasConfigurableOptions]
 * @returns {Object|undefined}
 */
export function resolveSimplePdpMainViewDetailsProduct({
  addOnDetailsProduct = null,
  mirrorViewDetailsProduct = null,
  committedCoverProduct = null,
  masterProduct = null,
  hasConfigurableOptions = false,
} = {}) {
  if (addOnDetailsProduct) {
    return addOnDetailsProduct;
  }

  const resolved = mirrorViewDetailsProduct ?? committedCoverProduct ?? null;

  if (hasConfigurableOptions) {
    return resolved ?? undefined;
  }

  return resolved ?? masterProduct ?? undefined;
}

/**
 * Whether to render ViewDetails below the Simple PDP main gallery (desktop).
 *
 * @param {Object} params
 * @param {Object|null} [params.addOnDetailsProduct]
 * @param {boolean} [params.hasConfigurableOptions]
 * @param {boolean} [params.isInlineCustomizePickerOpen]
 * @param {boolean} [params.isSimpleMasterWithCovers]
 * @param {Object|null|undefined} [params.committedSelections]
 * @param {Object|undefined} [params.viewDetailsProduct]
 * @param {string|null} [params.masterProductId]
 * @param {Object} [params.previewVariationValues]
 * @returns {boolean}
 */
export function resolveSimplePdpShowGallerySummary({
  addOnDetailsProduct = null,
  hasConfigurableOptions = false,
  isInlineCustomizePickerOpen = false,
  isSimpleMasterWithCovers = false,
  committedSelections = null,
  viewDetailsProduct = null,
  masterProductId = null,
  previewVariationValues = null,
} = {}) {
  if (addOnDetailsProduct) {
    return true;
  }

  if (hasConfigurableOptions) {
    if (isInlineCustomizePickerOpen) {
      return resolveShowViewDetailsSection({
        baselineVariationValues: committedSelections?.cover?.variationValues,
        configPanelMode: 'customize',
        effectiveSelectedId: masterProductId,
        hasCustomizeInteraction: true,
        masterProductId,
        previewVariationValues,
        viewDetailsProduct,
      });
    }

    // Cover overflow flyout is draft-until-Done — show when ViewDetails already
    // resolved to a non-master product (committed cover may still be null).
    if (isMeaningfulProductPreview(viewDetailsProduct, masterProductId)) {
      return true;
    }

    return hasUserVisibleCommittedSelectionData(committedSelections) &&
      Boolean(viewDetailsProduct);
  }

  if (isSimpleMasterWithCovers) {
    return Boolean(viewDetailsProduct);
  }

  return true;
}
